import { Fragment, ReactNode, useMemo, useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { Checkbox } from '@design-system';
import {
  getCumulativeLtcPayoutSeries,
  getMonthPayout,
} from '@shared/calculations/funding/projection/projectionTotals';
import { TooltipLabel } from '../TooltipLabel';
import { ClaimantBadge } from './ClaimantBadge';
import { monthRowKey } from './formatters';
import { AggRenderContext, ColumnGroup, ColumnSpec, MonthRenderContext } from './types';

interface ProjectionTableProps {
  title?: string;
  subtitle?: ReactNode;
  groups: ColumnGroup[];
  months: MonthlyEntry[];
  openStateForFirstMonth: MonthlyState;
  // Cumulative LTC payout BEFORE the first month in this view, so the
  // running "paid to date" badge accounts for any payouts that happened
  // earlier in the projection (e.g. the EP lump row that triggered the
  // pre→payout split).
  cumulativeLtcPayoutBeforeFirstMonth?: number;
  footer?: ReactNode;
  /**
   * When true, months are grouped by `month.context.policyYear`. Each year
   * renders as one expandable summary row (aggregated via each column's
   * `renderAggregate`); expanding reveals the monthly rows beneath. A
   * "Total" footer row is always rendered in this mode.
   */
  groupByPolicyYear?: boolean;
}

interface YearGroup {
  policyYear: number;
  months: MonthlyEntry[];
  openStateAtStart: MonthlyState;
  cumulativeAtStart: number;
}

export function ProjectionTable({
  title,
  subtitle,
  groups,
  months,
  openStateForFirstMonth,
  cumulativeLtcPayoutBeforeFirstMonth = 0,
  footer,
  groupByPolicyYear,
}: ProjectionTableProps) {
  const flatColumns = useMemo(() => groups.flatMap(g => g.columns), [groups]);

  // Group "bands" for the top header row. Adjacent groups sharing the same
  // `title` collapse into one band (Payout and Cost both live under "This
  // month", and CM II's strategy-extra surplus group joins "Running balances").
  // Tracks the index of the first column in each band so body / summary / total
  // rows can draw a strong left border on band-boundary cells.
  const bands = useMemo(() => {
    const out: { title: string; colSpan: number; startIndex: number }[] = [];
    let col = 0;
    for (const g of groups) {
      const last = out[out.length - 1];
      const title = g.title ?? '';
      if (last && last.title === title) {
        last.colSpan += g.columns.length;
      } else {
        out.push({ title, colSpan: g.columns.length, startIndex: col });
      }
      col += g.columns.length;
    }
    return out;
  }, [groups]);

  const bandStartIndexes = useMemo(
    () => new Set(bands.slice(1).map(b => b.startIndex)),
    [bands],
  );
  const isBandStart = (ci: number) => bandStartIndexes.has(ci);

  const [yearlyView, setYearlyView] = useState(false);
  const groupingActive = Boolean(groupByPolicyYear) && yearlyView;

  // Running cumulative LTC payout per month — offset by anything paid before
  // this view started. `series[i]` is "paid through month i, inclusive".
  const cumulativeSeries = useMemo<number[]>(() => {
    const local = getCumulativeLtcPayoutSeries(months);
    return local.map(v => v + cumulativeLtcPayoutBeforeFirstMonth);
  }, [months, cumulativeLtcPayoutBeforeFirstMonth]);

  const yearGroups = useMemo<YearGroup[] | null>(() => {
    if (!groupingActive) return null;
    const result: YearGroup[] = [];
    let cumulativeAtStart = cumulativeLtcPayoutBeforeFirstMonth;
    let cumulativeWalker = cumulativeLtcPayoutBeforeFirstMonth;
    months.forEach(month => {
      const last = result[result.length - 1];
      if (last && last.policyYear === month.context.policyYear) {
        last.months.push(month);
      } else {
        if (last) {
          cumulativeAtStart = cumulativeWalker;
        }
        // Read the group's first row's stored `openState` rather than the
        // close of the previous group's last row: with the per-claimant
        // payout split (and optional view filter) `months` is no longer a
        // contiguous slice, so "close of prior visible row" can skip over
        // interleaved spouse rows and report the wrong opening balance.
        result.push({
          policyYear: month.context.policyYear,
          months: [month],
          openStateAtStart: month.openState,
          cumulativeAtStart,
        });
      }
      cumulativeWalker += getMonthPayout(month);
    });
    return result;
  }, [months, cumulativeLtcPayoutBeforeFirstMonth, groupingActive]);

  const [expandedYears, setExpandedYears] = useState<Set<number>>(new Set());
  const isExpanded = (year: number) => expandedYears.has(year);
  const toggleYear = (year: number) =>
    setExpandedYears(prev => {
      const next = new Set(prev);
      if (next.has(year)) next.delete(year);
      else next.add(year);
      return next;
    });

  const showHeaderBar = Boolean(title || groupByPolicyYear);

  return (
    <div>
      {showHeaderBar && (
        <div className="flex items-center justify-between border-b border-gray-200 px-4 py-2">
          <div>
            {title && <span className="font-medium text-gray-900">{title}</span>}
            {subtitle && <span className="ml-2 text-sm text-gray-500">{subtitle}</span>}
          </div>
          <div className="flex items-center gap-3">
            {groupByPolicyYear && (
              <label className="flex cursor-pointer items-center gap-2 text-xs text-gray-700">
                <Checkbox
                  checked={yearlyView}
                  onCheckedChange={v => setYearlyView(v === true)}
                />
                Yearly view
              </label>
            )}
          </div>
        </div>
      )}
      <div className="max-h-[70vh] overflow-auto">
        <table className="min-w-full border-collapse text-xs">
          <thead className="sticky top-0 z-20 bg-white shadow-[0_1px_0_rgba(0,0,0,0.08)]">
            <tr>
              {bands.map((band, bi) => (
                <th
                  key={`band-${bi}`}
                  colSpan={band.colSpan}
                  className={`border-b border-gray-200 bg-gray-50 px-3 py-1.5 text-left text-[10px] font-semibold uppercase tracking-[0.08em] text-gray-500 ${
                    bi > 0 ? 'border-l-2 border-l-gray-300' : ''
                  }`}
                >
                  {band.title}
                </th>
              ))}
            </tr>
            <tr>
              {flatColumns.map((col, ci) => {
                const stickyClasses = col.sticky
                  ? 'sticky left-0 z-10 bg-white shadow-[1px_0_0_rgba(0,0,0,0.06)]'
                  : '';
                const bandClass = isBandStart(ci) ? 'border-l-2 border-l-gray-300' : '';
                return (
                  <th
                    key={col.key}
                    className={`${stickyClasses} ${bandClass} whitespace-nowrap border-r border-gray-200 px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wide text-gray-700`}
                  >
                    <TooltipLabel
                      label={col.label}
                      labelSuffix={col.labelSuffix}
                      tooltip={col.tooltip}
                    />
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {!yearGroups &&
              months.map((month, i) => {
                // Each row carries its true opening state; reading it directly
                // stays correct when `months` is a non-contiguous (joint /
                // view-filtered) slice, where `months[i - 1]` is not the row
                // that actually preceded this one in the projection.
                const openState = month.openState;
                return (
                  <DetailRow
                    key={monthRowKey(month)}
                    columns={flatColumns}
                    month={month}
                    openState={openState}
                    cumulativeLtcPayoutToDate={cumulativeSeries[i]}
                    isBandStart={isBandStart}
                  />
                );
              })}
            {yearGroups &&
              yearGroups.map(group => {
                const expanded = isExpanded(group.policyYear);
                const lastIdxInGroup =
                  months.length - 1 -
                  [...months].reverse().findIndex(m => m.context.policyYear === group.policyYear);
                const cumulativeAtEnd = cumulativeSeries[lastIdxInGroup];
                return (
                  <Fragment key={group.policyYear}>
                    <SummaryRow
                      columns={flatColumns}
                      label={`Year ${group.policyYear}`}
                      months={group.months}
                      openStateAtStart={group.openStateAtStart}
                      cumulativeLtcPayoutAtEnd={cumulativeAtEnd}
                      expanded={expanded}
                      onToggle={() => toggleYear(group.policyYear)}
                      isBandStart={isBandStart}
                    />
                    {expanded &&
                      group.months.map((month, i) => {
                        const openState = month.openState;
                        const globalIdx =
                          months.indexOf(group.months[0]) + i;
                        return (
                          <DetailRow
                            key={monthRowKey(month)}
                            columns={flatColumns}
                            month={month}
                            openState={openState}
                            cumulativeLtcPayoutToDate={cumulativeSeries[globalIdx]}
                            indent
                            isBandStart={isBandStart}
                          />
                        );
                      })}
                  </Fragment>
                );
              })}
          </tbody>
          <tfoot>
            {groupByPolicyYear && months.length > 0 && (
              <TotalRow
                columns={flatColumns}
                months={months}
                openStateAtStart={openStateForFirstMonth}
                cumulativeLtcPayoutAtEnd={cumulativeSeries[cumulativeSeries.length - 1]}
                isBandStart={isBandStart}
              />
            )}
            {footer}
          </tfoot>
        </table>
      </div>
    </div>
  );
}

function DetailRow({
  columns,
  month,
  openState,
  cumulativeLtcPayoutToDate,
  indent,
  isBandStart,
}: {
  columns: ColumnSpec[];
  month: MonthlyEntry;
  openState: MonthlyState;
  cumulativeLtcPayoutToDate: number;
  indent?: boolean;
  isBandStart: (ci: number) => boolean;
}) {
  const ctx: MonthRenderContext = { month, openState, cumulativeLtcPayoutToDate };
  return (
    <tr className="border-b border-gray-100 bg-white hover:bg-gray-50">
      {columns.map((col, ci) => {
        const stickyClasses = col.sticky
          ? 'sticky left-0 z-[1] bg-white shadow-[1px_0_0_rgba(0,0,0,0.06)]'
          : '';
        const indentClass = indent && ci === 0 ? 'pl-8' : '';
        const bandClass = isBandStart(ci) ? 'border-l-2 border-l-gray-300' : '';
        // Joint policies tag each row with a spouse; show a badge in the first
        // column so interleaved rows are distinguishable. Single-life rows have
        // no claimant and render exactly as before.
        const showClaimant = ci === 0 && month.context.claimant != null;
        return (
          <td
            key={col.key}
            className={`${stickyClasses} ${indentClass} ${bandClass} whitespace-nowrap border-r border-gray-100 px-2 py-1 align-middle text-gray-800`}
          >
            {showClaimant ? (
              <div className="flex items-center gap-1.5">
                <ClaimantBadge claimant={month.context.claimant} />
                {col.render(ctx)}
              </div>
            ) : (
              col.render(ctx)
            )}
          </td>
        );
      })}
    </tr>
  );
}

function SummaryRow({
  columns,
  label,
  months,
  openStateAtStart,
  cumulativeLtcPayoutAtEnd,
  expanded,
  onToggle,
  isBandStart,
}: {
  columns: ColumnSpec[];
  label: string;
  months: MonthlyEntry[];
  openStateAtStart: MonthlyState;
  cumulativeLtcPayoutAtEnd: number;
  expanded: boolean;
  onToggle: () => void;
  isBandStart: (ci: number) => boolean;
}) {
  const ctx: AggRenderContext = {
    months,
    openStateAtStart,
    closeStateAtEnd: months[months.length - 1].state,
    cumulativeLtcPayoutAtEnd,
    label,
    kind: 'year',
  };
  return (
    <tr
      className="cursor-pointer border-y border-gray-200 bg-primary/5 hover:bg-primary/10"
      onClick={onToggle}
    >
      {columns.map((col, ci) => {
        const stickyClasses = col.sticky
          ? 'sticky left-0 z-[1] bg-primary/5 shadow-[1px_0_0_rgba(0,0,0,0.06)]'
          : '';
        const bandClass = isBandStart(ci) ? 'border-l-2 border-l-gray-300' : '';
        return (
          <td
            key={col.key}
            className={`${stickyClasses} ${bandClass} whitespace-nowrap border-r border-gray-100 px-2 py-3 align-middle text-gray-900`}
          >
            {ci === 0 ? (
              <div className="flex items-center gap-1.5">
                <ChevronRight
                  className={`h-3.5 w-3.5 shrink-0 text-gray-700 transition-transform ${
                    expanded ? 'rotate-90' : ''
                  }`}
                />
                {col.renderAggregate ? col.renderAggregate(ctx) : <span>{label}</span>}
              </div>
            ) : col.renderAggregate ? (
              col.renderAggregate(ctx)
            ) : (
              <span className="text-gray-300">—</span>
            )}
          </td>
        );
      })}
    </tr>
  );
}

function TotalRow({
  columns,
  months,
  openStateAtStart,
  cumulativeLtcPayoutAtEnd,
  isBandStart,
}: {
  columns: ColumnSpec[];
  months: MonthlyEntry[];
  openStateAtStart: MonthlyState;
  cumulativeLtcPayoutAtEnd: number;
  isBandStart: (ci: number) => boolean;
}) {
  const ctx: AggRenderContext = {
    months,
    openStateAtStart,
    closeStateAtEnd: months[months.length - 1].state,
    cumulativeLtcPayoutAtEnd,
    label: 'Total',
    kind: 'total',
  };
  return (
    <tr className="border-t-2 border-gray-300 bg-gray-50 font-semibold">
      {columns.map((col, ci) => {
        const stickyClasses = col.sticky
          ? 'sticky left-0 z-[1] bg-gray-50 shadow-[1px_0_0_rgba(0,0,0,0.06)]'
          : '';
        const bandClass = isBandStart(ci) ? 'border-l-2 border-l-gray-300' : '';
        return (
          <td
            key={col.key}
            className={`${stickyClasses} ${bandClass} whitespace-nowrap border-r border-gray-200 px-2 py-2 align-middle text-gray-900`}
          >
            {col.renderAggregate ? col.renderAggregate(ctx) : <span className="text-gray-300">—</span>}
          </td>
        );
      })}
    </tr>
  );
}
